import React, { useState } from "react";
import classNames from "classnames";
import { getInitials } from "@/utils/textUtils";
import type { UserBadgeProps } from "./types";
import styles from "./UserBadge.module.css";

/**
 * UserBadge - A user identifier component.
 *
 * By default, displays as a full-name chip (pill-shaped with border).
 * Can be set to avatar mode (initials in circle) or auto mode
 * (switches based on container width for mobile).
 *
 * @example
 * ```tsx
 * // Default chip mode - always shows full name
 * <UserBadge name="Christian Skaiaa" />
 *
 * // Avatar mode - always shows initials
 * <UserBadge name="Christian Skaiaa" mode="avatar" />
 *
 * // Auto mode - switches based on container width
 * <UserBadge name="Christian Skaiaa" mode="auto" />
 * ```
 */
export const UserBadge = React.forwardRef<HTMLDivElement, UserBadgeProps>(
  (
    {
      name,
      src,
      mode = "chip",
      size = "md",
      expandDirection = "right",
      clickable = false,
      selected = false,
      onClick,
      className,
      "data-testid": testId,
    },
    ref,
  ) => {
    const [imageError, setImageError] = useState(false);

    const initials = getInitials(name);
    const displayName = name.toUpperCase();
    const showImage = src && !imageError;

    const handleClick = () => {
      if (clickable && onClick) {
        onClick();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && clickable) {
        e.preventDefault();
        onClick?.();
      }
    };

    const handleImageError = () => {
      setImageError(true);
    };

    return (
      <div
        ref={ref}
        className={classNames(styles.userBadge, className)}
        data-mode={mode}
        data-size={size}
        data-expand={expandDirection}
        data-clickable={clickable ? "true" : undefined}
        data-selected={selected ? "true" : undefined}
        data-testid={testId}
        onClick={clickable ? handleClick : undefined}
        onKeyDown={clickable ? handleKeyDown : undefined}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
      >
        {/* Chip Mode - Full name display */}
        <span className={styles.chipMode}>
          <span className={styles.label}>{displayName}</span>
        </span>

        {/* Avatar Mode - Initials/image with hover-expand */}
        <span
          className={styles.avatarMode}
          data-hover-text={displayName}
          aria-label={name}
        >
          {showImage ? (
            <img
              src={src}
              alt={name}
              className={styles.avatarImage}
              onError={handleImageError}
            />
          ) : (
            <span className={styles.initials}>{initials}</span>
          )}
        </span>
      </div>
    );
  },
);

UserBadge.displayName = "UserBadge";

export default UserBadge;
